import { CloningContents, FilterCourse } from 'components/CourseDashboard';
import { useAuth } from 'contexts/auth';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useStripe } from 'contexts/stripe';
import { useViewport } from 'contexts/viewport';
import _ from 'lodash';
import moment from 'moment';
import { PageContext } from 'pages/CourseDashboard/PageProvider';
import Desktop from 'pages/CourseDashboard/desktop';
import Mobile from 'pages/CourseDashboard/mobile';
import Tablet from 'pages/CourseDashboard/tablet';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import courseService from 'services/course';
import { AlertDialog, Drawer } from 'shell/components';

const PageLayout = () => {
    const { currentUser, selectAccount, startCourseSetup } = useAuth();
    const { setJoyrideRun, setJoyrideStepIndex, setJoyridePaused } = useJoyride();
    const location = useLocation();
    const { message } = useSnackbar();
    const stripeContext = useStripe();
    const { device } = useViewport();

    const { cloningContentsDialog, setCloningContentsDialog, dialog, setDialog, generatedIdList, setGeneratedIdList } =
        useContext(PageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [courses, setCourses] = useState(null);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [sortKey, setSortKey] = useState('titlea-z');
    const [filterKey, setFilterKey] = useState([]);
    const [searchBarInput, setSearchBarInput] = useState('');

    const handleCourseCardDropdown = ({ key, courseId }) => {
        const courseObj = courses.find((course) => course.course_id === courseId);
        switch (key) {
            case 'duplicate-course': {
                if (courseObj.finished_course_set_up === false && courseObj?.role === 'instructor')
                    return message.error('Please finish course set up');
                return startCourseSetup(courseObj);
            }
            case 'delete': {
                return openDeleteCourseDialog(courseObj);
            }
            default:
                return;
        }
    };

    const deleteCourse = async (courseId) => {
        setCourses((prev) => prev.filter((course) => course.course_id !== courseId));
        if (!courses.length) {
            setIsLoading(false);
        }
        const deletingCourse = courses.find((course) => course.course_id === courseId);
        await courseService.deleteCourse(courseId, { course_id: courseId });
        return message.success(`${_.capitalize(deletingCourse.name || 'The new course')} was deleted successfully.`);
    };

    const openDeleteCourseDialog = (course) => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Delete ${course.name || 'this course'}?`,
            description:
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
            variant: 'destructive',
            actionButtonText: 'Delete',
            onAction: () => {
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                return deleteCourse(course.course_id);
            },
        }));
    };

    const onChangeSearchBarInput = (value) => {
        if (value === '' || !value) setSearchBarInput('');
        else setSearchBarInput(value);
    };

    const handleFilterClick = (key, data) => {
        switch (key) {
            case 'SORT': {
                return setSortKey(data);
            }
            case 'STATUS': {
                const hasItem = filterKey.includes(data);
                let newData = [...filterKey];
                if (hasItem) {
                    newData = newData.filter((item) => item !== data);
                } else {
                    newData = [...newData, data];
                }
                return setFilterKey(newData);
            }
            default:
                return;
        }
    };

    const handleResetFilterSort = () => {
        setSortKey('titlea-z');
        setFilterKey([]);
    };

    const getSortedFilteredcourses = () => {
        return courses
            ?.filter((course) => {
                if (searchBarInput) {
                    return course?.name?.toLowerCase().includes(searchBarInput.toLowerCase());
                }
                return true;
            })
            .filter((course) => {
                if (filterKey.length === 0) return true;
                const today = moment(new Date()).hour(0).minute(0).second(0).millisecond(0);
                const startDate = moment(course.start_on);
                const endDate = moment(course.end_on);
                if (filterKey.includes('inProgress')) {
                    return today.isSameOrAfter(startDate) && today.isSameOrBefore(endDate);
                }
                if (filterKey.includes('future')) {
                    return today.isBefore(startDate);
                }
                if (filterKey.includes('past')) {
                    return today.isAfter(endDate);
                }
                return false;
            })
            .sort((a, b) => {
                if (sortKey === 'titlea-z') {
                    return a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1;
                }
                if (sortKey === 'titlez-a') {
                    return a.name?.toLowerCase() < b.name?.toLowerCase() ? 1 : -1;
                }

                return 0;
            });
    };

    useEffect(() => {
        (async () => {
            const data = await courseService.listCourses();
            if (!data) return;
            const accountCourses = await Promise.all(
                data.accounts
                    .filter((account) => account.active)
                    .filter((account) => account.role !== 'student')
                    .map(async (account) => {
                        const course = data.courses
                            .filter((course) => course.active !== false) // some old course documents created from self serve admin are missing `active`
                            .find((course) => course.id === account.course_id);
                        const courseAccounts = await courseService.findCourseAccounts(account.course_id);
                        return {
                            user_id: account.user_id,
                            owner_account_id: account.id,
                            role: account.role,
                            course_id: account.course_id,
                            generated_id: course.generated_id || null,
                            group_id: account.group_id,
                            school: course.school,
                            faculty: course.faculty,
                            name: course.name,
                            semester: course.semester,
                            field_of_study: course.field_of_study,
                            section: course.section,
                            project_title: course.project_title,
                            start_on: course.start_on,
                            end_on: course.end_on,
                            finished_course_set_up: course.finished_course_set_up !== false,
                            group_formation: course.group_formation || null,
                            survey: course.survey || null,
                            finished_group_generation: course.finished_group_generation || false,
                            accounts: courseAccounts,
                        };
                    })
            );
            setCourses(accountCourses);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (currentUser?.finished_onboarding) return;
        if (stripeContext?.paymentDialog?.open) return;

        if (location.state?.showWelcomeDialog) {
            stripeContext.setWelcomeDialog(true);
            setJoyridePaused(true);
        } else {
            if (courses) {
                setJoyrideRun(true);
                setJoyridePaused(false);
            }
            if (courses?.length) {
                setJoyrideStepIndex(1);
            }
        }
    }, [
        courses,
        currentUser?.finished_onboarding,
        location.state?.showWelcomeDialog,
        setJoyridePaused,
        setJoyrideRun,
        setJoyrideStepIndex,
        stripeContext,
        stripeContext?.paymentDialog?.open,
        stripeContext.setWelcomeDialog,
    ]);

    useEffect(() => {
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    const props = {
        isLoading,
        courses,
        filteredCourses: getSortedFilteredcourses(),
        startCourseSetup,
        searchBarInput,
        onChangeSearchBarInput,
        setIsFilterDrawerOpen,
        selectCourse: (course) => {
            setIsLoading(true);
            selectAccount(course.owner_account_id);
        },
        handleCourseCardDropdown,
        filterKey,
        isFilterDrawerOpen,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    const filterDrawerProps = {
        open: isFilterDrawerOpen,
        onClose: () => setIsFilterDrawerOpen(false),
    };

    const FilterDrawerInner = (
        <FilterCourse
            sortKey={sortKey}
            filterKey={filterKey}
            onClick={handleFilterClick}
            onClose={() => setIsFilterDrawerOpen(false)}
            resetFilterSort={handleResetFilterSort}
        />
    );

    const renderResponsiveFilterDrawers = (device) => {
        return {
            mobile: (
                <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
            tablet: (
                <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
            desktop: (
                <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
        }[device];
    };

    return (
        <>
            {renderResponsiveLayout(device)}
            {renderResponsiveFilterDrawers(device)}
            <AlertDialog
                message={dialog.message}
                description={dialog.description}
                open={dialog.open}
                variant={dialog.variant}
                actionButtonText={dialog.actionButtonText}
                onAction={dialog.onAction}
                onCancel={() => setDialog((current) => ({ ...current, open: false }))}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
            />
            <CloningContents
                open={cloningContentsDialog.open}
                setOpen={setCloningContentsDialog}
                course={cloningContentsDialog.course}
                dialog={dialog}
                setDialog={setDialog}
                generatedIdList={generatedIdList}
                setGeneratedIdList={setGeneratedIdList}
            />
        </>
    );
};

export default PageLayout;
